package co.uk.paxes.pinpoint.web

import kotlinx.browser.document

/**
 * Screen types for navigation routing.
 * Matches the design document Screen type definition.
 */
enum class Screen(val id: String) {
    LANDING("landing"),
    MAIN_MENU("main-menu"),
    ROOM_LIST("room-list"),
    ROOM_LOBBY("room-lobby"),
    GAME("game"),
    AI_DIFFICULTY("ai-difficulty"),
    AI_GAME("ai-game"),
    LOCAL_SETUP("local-setup"),
    LOCAL_GAME("local-game"),
    PASS_PLAY("pass-play"),
    LEADERBOARD("leaderboard"),
    HOW_TO_PLAY("how-to-play"),
    TIPS("tips"),
    SETTINGS("settings");

    companion object {
        fun fromId(id: String): Screen? = entries.find { it.id == id }
    }
}

/**
 * NavigationManager handles screen navigation and routing.
 * Implements the interface defined in the design document.
 * 
 * Requirements: 17.5, 17.6, 17.7
 */
object NavigationManager {
    private val screenHistory = mutableListOf(Screen.LANDING)
    private var currentScreen = Screen.LANDING
    
    // Callback for screen change events (allows other components to react)
    var onScreenChange: ((Screen, Screen?) -> Unit)? = null

    /**
     * Navigate to a screen by Screen enum.
     * Adds the screen to history and shows it.
     * 
     * Requirements: 17.5, 17.6
     */
    fun navigateTo(screen: Screen) {
        val previousScreen = currentScreen
        
        // Hide current screen
        hideScreen(currentScreen)
        
        // Show new screen
        showScreen(screen)
        
        // Update history and current
        screenHistory.add(screen)
        currentScreen = screen
        
        // Notify listeners
        onScreenChange?.invoke(screen, previousScreen)
    }

    /**
     * Navigate to a screen by string ID.
     * Convenience method for HTML onclick handlers.
     * 
     * Requirements: 17.5, 17.6
     */
    fun navigateTo(screenId: String) {
        val screen = Screen.fromId(screenId)
        if (screen != null) {
            navigateTo(screen)
        } else {
            console.error("Unknown screen: $screenId")
        }
    }

    /**
     * Go back to the previous screen.
     * Removes current screen from history and shows previous.
     * 
     * Requirements: 17.7
     */
    fun goBack() {
        if (screenHistory.size <= 1) {
            // Already at root, can't go back
            return
        }
        
        val previousScreen = currentScreen
        
        // Hide current screen
        hideScreen(currentScreen)
        
        // Remove current from history
        screenHistory.removeLast()
        
        // Get and show previous screen
        val newScreen = screenHistory.last()
        showScreen(newScreen)
        currentScreen = newScreen
        
        // Notify listeners
        onScreenChange?.invoke(newScreen, previousScreen)
    }

    /**
     * Get the current screen.
     */
    fun getCurrentScreen(): Screen = currentScreen

    /**
     * Get the current screen ID string.
     */
    fun getCurrentScreenId(): String = currentScreen.id

    /**
     * Check if we can go back (not at root).
     */
    fun canGoBack(): Boolean = screenHistory.size > 1

    /**
     * Get the screen history depth.
     */
    fun getHistoryDepth(): Int = screenHistory.size

    /**
     * Reset navigation to main menu.
     * Clears history and shows main menu.
     */
    fun resetToMainMenu() {
        hideScreen(currentScreen)
        screenHistory.clear()
        screenHistory.add(Screen.MAIN_MENU)
        currentScreen = Screen.MAIN_MENU
        showScreen(Screen.MAIN_MENU)
        onScreenChange?.invoke(Screen.MAIN_MENU, null)
    }

    /**
     * Hide a screen by removing the 'active' class.
     */
    private fun hideScreen(screen: Screen) {
        document.getElementById("screen-${screen.id}")?.classList?.remove("active")
    }

    /**
     * Show a screen by adding the 'active' class.
     */
    private fun showScreen(screen: Screen) {
        document.getElementById("screen-${screen.id}")?.classList?.add("active")
        updateBodyInGameClass(screen)
        
        // Call screen-specific initialization
        when (screen) {
            Screen.LOCAL_SETUP -> {
                // Initialize local game setup UI
                try {
                    js("if (typeof window.initLocalSetup === 'function') window.initLocalSetup();")
                } catch (e: Exception) {
                    console.error("Failed to initialize local setup: ${e.message}")
                }
            }
            else -> {}
        }
    }

    private fun updateBodyInGameClass(screen: Screen) {
        val isGameScreen = screen == Screen.GAME || screen == Screen.LOCAL_GAME || 
                           screen == Screen.AI_GAME || screen == Screen.PASS_PLAY
        if (isGameScreen) {
            document.body?.classList?.add("in-game")
        } else {
            document.body?.classList?.remove("in-game")
        }
    }
}
